import Button from 'app/components/Button'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import Spinner from 'app/components/Spinner'
import { CopyToClipboard } from 'app/components/shared/CopyToClipboard'
import { closeModal } from 'app/lib/modal'
import type { UseDniFormReturn } from './useDniForm'

export type DniSummaryViewProps = UseDniFormReturn

export function DniSummaryView(props: DniSummaryViewProps) {
  const { dniData, loadingValues, snippetCode, removing, closing, setMode, onDisable, replacesNumberDisplay } = props
  const autoScaleOn = !!dniData?.maxPoolSize && dniData.maxPoolSize > (dniData.poolSize ?? 0)

  return (
    <>
      <div className="w-150 space-y-5 px-6 py-4">
        <KeyValueDrawer
          cols={2}
          map={{
            'Replaces Number': replacesNumberDisplay || `\u2014`,
            'Display Name': dniData?.label || `\u2014`,
            'Starting Pool Size': String(dniData?.poolSize ?? `\u2014`),
            'Session Duration': dniData?.sessionDuration ? `${dniData.sessionDuration} min` : `\u2014`,
          }}
        />

        <div className="flex flex-col gap-y-1">
          <div className="text-body-xs text-subtle">Pool Numbers</div>
          {loadingValues ? (
            <div className="flex h-6 items-center">
              <Spinner size={4} />
            </div>
          ) : dniData?.poolNumbers?.length ? (
            <>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {dniData.poolNumbers.map((p) => (
                  <span key={p.phone} className="inline-flex items-center gap-1.5 text-ui-xs text-strong">
                    {p.formatted}
                    {p.autoProvisioned && (
                      <span className="rounded bg-info-subtle px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-info-default">
                        Auto
                      </span>
                    )}
                  </span>
                ))}
              </div>
              {dniData.poolNumbers.length < (dniData.poolSize ?? 0) && (
                <div className="mt-1 flex items-center gap-2 text-ui-xs text-subtle">
                  <Spinner size={4} />
                  Buying more numbers in process. Re-open this modal in a few seconds.
                </div>
              )}
            </>
          ) : (dniData?.poolSize ?? 0) > 0 ? (
            <div className="flex items-center gap-2 text-ui-xs text-subtle">
              <Spinner size={4} />
              Buying numbers in process. Re-open this modal in a few seconds.
            </div>
          ) : (
            <div className="text-ui-xs text-subtle">No pool numbers provisioned</div>
          )}
        </div>

        <div className="flex flex-col gap-y-1">
          <div className="text-body-xs text-subtle">Embed Code</div>
          <div className="flex items-center gap-2 rounded-md border border-default bg-surface-subtle px-3 py-2">
            <code className="flex-1 text-xs text-default">{snippetCode}</code>
            {snippetCode && <CopyToClipboard text={snippetCode} />}
          </div>
          <p className="text-xs text-subtle">Add this script tag to every page of the dental office&apos;s website.</p>
        </div>

        <div className="flex flex-col gap-y-1">
          <div className="text-body-xs text-subtle">Auto-scale</div>
          {autoScaleOn ? (
            <div className="rounded-md border border-default bg-surface-subtle px-3 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-success-default" />
                <span className="text-ui-sm font-medium text-strong">Auto-scale is on</span>
              </div>
              <div className="mt-3 grid grid-cols-4 gap-x-4">
                <div className="flex flex-col">
                  <span className="min-h-9 text-ui-xs text-subtle">Max numbers</span>
                  <span className="text-ui-sm font-medium text-strong">{dniData?.maxPoolSize}</span>
                </div>
                <div className="flex flex-col">
                  <span className="min-h-9 text-ui-xs text-subtle">Buys when free is below</span>
                  <span className="text-ui-sm font-medium text-strong">{dniData?.minFree ?? 1}</span>
                </div>
                <div className="flex flex-col">
                  <span className="min-h-9 text-ui-xs text-subtle">Buys at a time</span>
                  <span className="text-ui-sm font-medium text-strong">{dniData?.buyStep ?? 1}</span>
                </div>
                <div className="flex flex-col">
                  <span className="min-h-9 text-ui-xs text-subtle">Release floor</span>
                  <span className="text-ui-sm font-medium text-strong">
                    {dniData?.minPoolSize ?? dniData?.poolSize ?? 1}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-default bg-surface-subtle px-3 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-surface-strong" />
                <span className="text-ui-sm font-medium text-strong">Auto-scale is off</span>
              </div>
              <p className="mt-2 text-ui-xs text-subtle">
                This pool never buys numbers on its own. When every number is busy, visitors reuse the soonest-expiring
                number. Edit the pool and set a max above the starting pool size to turn auto-scale on.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <Button
              label="Disable Pool"
              mode="danger"
              size="md"
              onClick={() => onDisable()}
              loading={removing || closing}
              disabled={removing || closing}
            />
          </div>
          <div className="flex gap-2">
            <Button
              label="Edit"
              mode="white"
              size="md"
              onClick={() => setMode(`form`)}
              disabled={removing || closing}
            />
            <Button
              label="Close"
              mode="primary"
              size="md"
              onClick={() => closeModal()}
              disabled={removing || closing}
            />
          </div>
        </div>
      </div>
    </>
  )
}
